import Async from "components/DesignSystem/AsyncComponents/Async";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import Wind from "components/icons/Wind";
import {
  BULK_INVOICE_ACTION,
  BULK_INVOICE_TYPE,
  INVOICE_STATUSES,
} from "constants/invoicing";
import { useInvoiceBulkAction } from "hooks/useInvoiceBulkAction";
import { useToast } from "hooks/useToast";
import { useMemo, useState } from "react";
import { RowSelectionState } from "react-table-8.10.7";
import { useBulkUpdateInvoiceMutation } from "store/apis/invoices";
import { BackendError } from "types/utils/error";
import { pluralize } from "utils/pluralize";
import { InvoiceTable } from "./InvoiceTable";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ArrowDown } from "components/icons/ArrowDown";
import { DeleteInvoiceWarningModal } from "./DeleteInvoiceWarningModal";
import dayjs from "dayjs";
import { Info } from "components/icons/Info";

export default function InvoiceDeleteModal({
  groupId,
  entityId,
  selectedRows,
  rowSelection,
  success,
}: {
  groupId: string;
  entityId: string;
  selectedRows: string[];
  rowSelection?: RowSelectionState;
  success: () => void;
}) {
  const [bulkUpdateOrDeleteInvoice, { isLoading: isDeletingInvoices }] =
    useBulkUpdateInvoiceMutation();
  const { alertToast, successToast } = useToast();

  const [rowSelectionState, setRowSelectionState] = useState(
    rowSelection || {}
  );

  const newSelectedRows = useMemo(
    () =>
      Object.entries(rowSelectionState)
        .filter(([_, isSelected]) => isSelected)
        .map(([uuid]) => uuid),
    [rowSelectionState]
  );

  const {
    allInvoices,
    isLoading,
    valid,
    invalid,
    totalInvoices,
    isAllInvoiceInvalid,
    shouldShowButton,
    shouldShowWarningModal,
    invoices,
  } = useInvoiceBulkAction({
    groupId,
    entityId,
    selectedRows,
    newSelectedRows: newSelectedRows,
    excludeStripeInvoices: false,
  });

  const selectedInvoices = useMemo(
    () => invoices.filter((inv) => newSelectedRows.includes(inv.uuid)),
    [invoices, newSelectedRows]
  );

  const draftInvoices = useMemo(
    () =>
      selectedInvoices.filter((inv) => inv.status === INVOICE_STATUSES.DRAFT),
    [selectedInvoices]
  );

  const dueOverdueInvoices = useMemo(
    () =>
      selectedInvoices.filter(
        (inv) =>
          inv.status === INVOICE_STATUSES.DUE ||
          inv.status === INVOICE_STATUSES.OVERDUE
      ),
    [selectedInvoices]
  );

  const isOnlyDueOverdue =
    dueOverdueInvoices.length > 0 &&
    draftInvoices.length === 0 &&
    selectedInvoices.length === dueOverdueInvoices.length;

  const onDelete = async (rowIds?: string[]) => {
    try {
      if (rowIds?.length === 0) return;
      await bulkUpdateOrDeleteInvoice({
        groupId: groupId!,
        entityId: entityId!,
        record_ids: rowIds ?? [],
        action: BULK_INVOICE_ACTION.DELETE,
        type: BULK_INVOICE_TYPE.INVOICE,
      }).unwrap();
      success();
      successToast({
        message: "Selected invoices deleted.",
      });
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const onVoid = async (rowIds?: string[]) => {
    try {
      if (rowIds?.length === 0) return;
      await bulkUpdateOrDeleteInvoice({
        groupId: groupId!,
        entityId: entityId!,
        record_ids: rowIds ?? [],
        action: BULK_INVOICE_ACTION.VOID,
        type: BULK_INVOICE_TYPE.INVOICE,
      }).unwrap();
      successToast({
        message: "Selected invoices voided.",
      });
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const handleDeleteDraftOnly = async () => {
    const draftIds = draftInvoices.map((inv) => inv.uuid);
    await onDelete(draftIds);
  };

  const handleDeleteDraftVoidOthers = async () => {
    const allSelectedIds = [...draftInvoices, ...dueOverdueInvoices].map(
      (inv) => inv.uuid
    );

    try {
      if (allSelectedIds.length === 0) return;
      await bulkUpdateOrDeleteInvoice({
        groupId: groupId!,
        entityId: entityId!,
        record_ids: allSelectedIds,
        action: BULK_INVOICE_ACTION.DELETE_DRAFT_VOID_OTHERS,
        type: BULK_INVOICE_TYPE.INVOICE,
      }).unwrap();
      success();
      successToast({
        message: "Draft invoices deleted and others voided.",
      });
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const handleDeleteAll = async () => {
    const allSelectedIds = [...draftInvoices, ...dueOverdueInvoices].map(
      (inv) => inv.uuid
    );
    await onDelete(allSelectedIds);
  };

  const handleVoidDueOverdue = async () => {
    const dueOverdueIds = dueOverdueInvoices.map((inv) => inv.uuid);
    await onVoid(dueOverdueIds);
    success();
  };

  const handleDeleteDueOverdue = async () => {
    const dueOverdueIds = dueOverdueInvoices.map((inv) => inv.uuid);
    await onDelete(dueOverdueIds);
  };

  if (isOnlyDueOverdue) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <DeleteInvoiceWarningModal
          onVoid={handleVoidDueOverdue}
          onDelete={handleDeleteDueOverdue}
          isVoidLoading={isDeletingInvoices}
          isDeleteLoading={isDeletingInvoices}
        />
      </div>
    );
  }

  if (shouldShowWarningModal) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <Modal.Content>
          <StatusModal type="warning" title="Delete invoices?">
            <div className="t-text-body-sm t-space-y-3">
              <div>
                The selection includes Due and Overdue invoices. Deleting these
                invoices will also delete all related journal entries and will
                impact your financials.
              </div>
              <div className="t-italic t-text-body-sm">
                To keep your live financials unchanged, consider voiding the
                invoice. Voiding automatically creates a reversal journal entry.
              </div>
              <div className="t-italic t-flex t-items-center t-text-body-sm">
                <Info />
                <span className="t-ml-2 ">
                  Invoices will be voided as of {dayjs().format("MMMM D, YYYY")}
                </span>
              </div>
            </div>
          </StatusModal>
          <Modal.Footer className="t-flex t-justify-end t-gap-2">
            <Modal.RawClose asChild>
              <Button>Cancel</Button>
            </Modal.RawClose>
            <Dropdown.Root>
              <Dropdown.Trigger asChild>
                <Button
                  customType="primary"
                  isLoading={isDeletingInvoices}
                  disabled={isDeletingInvoices}
                >
                  Proceed
                  <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out t-text-surface">
                    <ArrowDown color="currentColor" strokeWidth="1.5" />
                  </div>
                </Button>
              </Dropdown.Trigger>
              <Dropdown.Portal>
                <Dropdown.Content align="end" sideOffset={3}>
                  <Dropdown.Item onClick={handleDeleteDraftOnly}>
                    Delete draft only
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleDeleteDraftVoidOthers}>
                    Delete draft, void others
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleDeleteAll}>
                    <span className="t-text-red-50">Delete all</span>
                  </Dropdown.Item>
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>
          </Modal.Footer>
        </Modal.Content>
      </div>
    );
  }

  return (
    <>
      <Modal.Header>
        <Modal.Title>Delete invoices</Modal.Title>
        <Modal.Close />
      </Modal.Header>
      <Modal.Body>
        <Async.Root
          isLoading={isLoading}
          isSuccess={!isLoading && !!allInvoices}
          isEmpty={false}
          isError={false}
        >
          <Async.Success>
            {newSelectedRows.length > 0 && (
              <>
                {isAllInvoiceInvalid && (
                  <div className="t-flex t-items-center t-gap-2 t-flex-col t-pb-12 t-text-center">
                    <Wind className="t-text-neutral-40" />
                    Bulk delete is only available for Draft and Sent invoices.
                  </div>
                )}

                {valid.length > 0 && (
                  <>
                    <div className="t-mb-4">
                      Are you sure you want to delete the{" "}
                      <b>
                        {valid.length} out of {totalInvoices}{" "}
                      </b>{" "}
                      invoices? This action cannot be undone. <br />
                    </div>
                    {invalid.length > 0 && (
                      <div className="t-mb-4">
                        <Banner
                          type="warning"
                          description={
                            <span>
                              The remaining{" "}
                              {pluralize(
                                invalid.length,
                                "invoice is",
                                "invoices are"
                              )}{" "}
                              already active and hence will not be deleted.
                            </span>
                          }
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            <InvoiceTable
              invoicesData={allInvoices?.invoices}
              disabledRowIds={invalid.map((row) => row?.uuid)}
              rowSelection={rowSelectionState}
              setRowSelection={setRowSelectionState}
              showActionsColumn={false}
            />
          </Async.Success>
        </Async.Root>
      </Modal.Body>
      <Modal.FooterButtonGroup>
        <Modal.RawClose asChild>
          <Button>Cancel</Button>
        </Modal.RawClose>
        {shouldShowButton && (
          <Button
            customType="danger"
            onClick={() => onDelete(valid.map((row) => row.uuid))}
            isLoading={isDeletingInvoices}
            disabled={isDeletingInvoices}
          >
            Delete
          </Button>
        )}
      </Modal.FooterButtonGroup>
    </>
  );
}
